/**
 * Filter Store
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ReinfoEstado, TipoActividad, TipoPersona, CatastroLeyenda } from '@/config/constants';

interface LayerVisibility {
  plots: boolean;
  concessions: boolean;
  forestal: boolean;
  certificados: boolean;
  libre: boolean;
  predios: boolean;
  comunidades: boolean;
  sernanp: boolean;
  water: boolean;
  waterInfra: boolean;
  roads: boolean;
  mining: boolean;
  restrictions: boolean;
  userContent: boolean;
  elevation: boolean;
  orthophotos: boolean;
}

interface PlotFilters {
  estado: ReinfoEstado[];
  tipo_actividad: TipoActividad[];
  tipo_persona: TipoPersona | null;
  departamento: string | null;
  provincia: string | null;
  distrito: string | null;
  has_phones: boolean;
  has_enrichment: boolean;
  favorites_only: boolean;  // Client-side filter
}

interface ConcessionFilters {
  leyenda: CatastroLeyenda[];
  hectareas_min: number | null;
  hectareas_max: number | null;
  has_reinfo: boolean;
  favorites_only: boolean;  // Client-side filter
}

interface FilterState {
  // Layer visibility
  layers: LayerVisibility;
  
  // Layer opacity
  layerOpacity: Record<keyof LayerVisibility, number>;
  
  // Filters
  plotFilters: PlotFilters;
  concessionFilters: ConcessionFilters;
  
  // Actions - Layers
  toggleLayer: (layer: keyof LayerVisibility) => void;
  setLayerOpacity: (layer: keyof LayerVisibility, opacity: number) => void;
  setLayers: (layers: Partial<LayerVisibility>) => void;
  
  // Actions - Plot filters
  setPlotFilter: <K extends keyof PlotFilters>(key: K, value: PlotFilters[K]) => void;
  setPlotFilters: (filters: Partial<PlotFilters>) => void;
  resetPlotFilters: () => void;
  
  // Actions - Concession filters
  setConcessionFilter: <K extends keyof ConcessionFilters>(key: K, value: ConcessionFilters[K]) => void;
  setConcessionFilters: (filters: Partial<ConcessionFilters>) => void;
  resetConcessionFilters: () => void;
  
  // Actions - Global
  resetAllFilters: () => void;
  
  // Computed
  hasActiveFilters: () => boolean;
  getPlotFilterParams: () => Record<string, string | boolean | string[]>;
  getConcessionFilterParams: () => Record<string, string | boolean | number | string[]>;
}

const defaultLayers: LayerVisibility = {
  plots: true,
  concessions: true,
  forestal: false,
  certificados: false,
  libre: false,
  predios: false,
  comunidades: false,
  sernanp: false,
  water: true,
  waterInfra: true,
  roads: true,
  mining: true,
  restrictions: true,
  userContent: true,
  elevation: false,
  orthophotos: true,
};

const defaultLayerOpacity: Record<keyof LayerVisibility, number> = {
  plots: 1,
  concessions: 0.7,
  forestal: 0.5,
  certificados: 1,
  libre: 0.5,
  predios: 0.8,
  comunidades: 0.7,
  sernanp: 0.7,
  water: 0.7,
  waterInfra: 0.9,
  roads: 0.8,
  mining: 0.7,
  restrictions: 0.7,
  userContent: 1,
  elevation: 0.6,
  orthophotos: 0.9,
};

const defaultPlotFilters: PlotFilters = {
  estado: [],
  tipo_actividad: [],
  tipo_persona: null,
  departamento: null,
  provincia: null,
  distrito: null,
  has_phones: false,
  has_enrichment: false,
  favorites_only: false,
};

const defaultConcessionFilters: ConcessionFilters = {
  leyenda: [],
  hectareas_min: null,
  hectareas_max: null,
  has_reinfo: false,
  favorites_only: false,
};

export const useFilterStore = create<FilterState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        layers: defaultLayers,
        layerOpacity: defaultLayerOpacity,
        plotFilters: defaultPlotFilters,
        concessionFilters: defaultConcessionFilters,
        
        // Layer actions
        toggleLayer: (layer) => set((state) => ({
          layers: {
            ...state.layers,
            [layer]: !state.layers[layer],
          },
        })),
        
        setLayerOpacity: (layer, opacity) => set((state) => ({
          layerOpacity: {
            ...state.layerOpacity,
            [layer]: Math.max(0, Math.min(1, opacity)),
          },
        })),
        
        setLayers: (layers) => set((state) => ({
          layers: { ...state.layers, ...layers },
        })),
        
        // Plot filter actions
        setPlotFilter: (key, value) => set((state) => ({
          plotFilters: {
            ...state.plotFilters,
            [key]: value,
          },
        })),
        
        setPlotFilters: (filters) => set((state) => ({
          plotFilters: { ...state.plotFilters, ...filters },
        })),
        
        resetPlotFilters: () => set({
          plotFilters: defaultPlotFilters,
        }),
        
        // Concession filter actions
        setConcessionFilter: (key, value) => set((state) => ({
          concessionFilters: {
            ...state.concessionFilters,
            [key]: value,
          },
        })),
        
        setConcessionFilters: (filters) => set((state) => ({
          concessionFilters: { ...state.concessionFilters, ...filters },
        })),
        
        resetConcessionFilters: () => set({
          concessionFilters: defaultConcessionFilters,
        }),
        
        // Global reset
        resetAllFilters: () => set({
          plotFilters: defaultPlotFilters,
          concessionFilters: defaultConcessionFilters,
        }),
        
        // Check if any filters are active
        hasActiveFilters: () => {
          const { plotFilters, concessionFilters } = get();
          
          const hasPlotFilters = 
            plotFilters.estado.length > 0 ||
            plotFilters.tipo_actividad.length > 0 ||
            plotFilters.tipo_persona !== null ||
            plotFilters.departamento !== null ||
            plotFilters.has_phones ||
            plotFilters.has_enrichment ||
            plotFilters.favorites_only;
          
          const hasConcessionFilters =
            concessionFilters.leyenda.length > 0 ||
            concessionFilters.hectareas_min !== null ||
            concessionFilters.hectareas_max !== null ||
            concessionFilters.has_reinfo;
          
          return hasPlotFilters || hasConcessionFilters;
        },
        
        // Get plot filters as API params
        getPlotFilterParams: () => {
          const { plotFilters } = get();
          const params: Record<string, string | boolean | string[]> = {};
          
          if (plotFilters.estado.length > 0) {
            params.estado = plotFilters.estado;
          }
          if (plotFilters.tipo_actividad.length > 0) {
            params.tipo_actividad = plotFilters.tipo_actividad;
          }
          if (plotFilters.tipo_persona) {
            params.tipo_persona = plotFilters.tipo_persona;
          }
          if (plotFilters.departamento) {
            params.departamento = plotFilters.departamento;
          }
          if (plotFilters.provincia) {
            params.provincia = plotFilters.provincia;
          }
          if (plotFilters.distrito) {
            params.distrito = plotFilters.distrito;
          }
          if (plotFilters.has_phones) {
            params.has_phones = true;
          }
          if (plotFilters.has_enrichment) {
            params.has_enrichment = true;
          }
          
          return params;
        },
        
        // Get concession filters as API params
        getConcessionFilterParams: () => {
          const { concessionFilters } = get();
          const params: Record<string, string | boolean | number | string[]> = {};
          
          if (concessionFilters.leyenda.length > 0) {
            params.leyenda = concessionFilters.leyenda;
          }
          if (concessionFilters.hectareas_min !== null) {
            params.hectareas_min = concessionFilters.hectareas_min;
          }
          if (concessionFilters.hectareas_max !== null) {
            params.hectareas_max = concessionFilters.hectareas_max;
          }
          if (concessionFilters.has_reinfo) {
            params.has_reinfo = true;
          }
          
          return params;
        },
      }),
      {
        name: 'reinfo-filters',
        partialize: (state) => ({
          layers: state.layers,
          layerOpacity: state.layerOpacity,
          plotFilters: state.plotFilters,
          concessionFilters: state.concessionFilters,
        }),
      }
    ),
    { name: 'FilterStore' }
  )
);

// Selectors
export const selectLayers = (state: FilterState) => state.layers;
export const selectPlotFilters = (state: FilterState) => state.plotFilters;
export const selectConcessionFilters = (state: FilterState) => state.concessionFilters;
